import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Instagram } from "lucide-react";
import { api } from "../lib/api";
import Seo, { absUrl } from "../lib/seo";
import EnquiryForm from "../components/EnquiryForm";

export default function ArtworkDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [art, setArt] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setArt(null); setErr(false);
    api.get(`/artworks/${slug}`).then((r) => setArt(r.data)).catch(async () => {
      // check for a 301 redirect (canonical URL changed)
      try {
        const res = await api.get(`/resolve/${slug}`);
        if (res.data.to) { navigate(`/archive/${res.data.to}`, { replace: true }); return; }
      } catch (e) {}
      setErr(true);
    });
  }, [slug, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (!art) return;
      if (e.key === "ArrowLeft" && art.prev) navigate(`/archive/${art.prev.slug}`);
      if (e.key === "ArrowRight" && art.next) navigate(`/archive/${art.next.slug}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [art, navigate]);

  if (err) return (
    <main className="pt-52 text-center label min-h-[50vh]" data-testid="artwork-notfound">
      <Seo title="Not found — RCKT" noindex />
      Work not found — <Link to="/archive" className="underline">back to archive</Link>
    </main>
  );
  if (!art) return <main className="pt-52 text-center label min-h-[50vh]">Loading…</main>;

  const canonical = `${window.location.origin}/archive/${art.slug}`;
  const seriesLabel = art.collection === "genesis" ? "Genesis"
    : art.collection.charAt(0).toUpperCase() + art.collection.slice(1);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VisualArtwork",
        "@id": `${canonical}#artwork`,
        "name": `${art.number_label} — ${art.title}`,
        "identifier": art.number_label,
        "url": canonical,
        "image": absUrl(art.eff_og_image),
        "description": art.description || art.eff_seo_description,
        "artform": art.category || "Drawing",
        "artMedium": art.medium || undefined,
        "creator": { "@type": "Person", "name": "RCKT", "url": window.location.origin + "/" },
        "dateCreated": art.year || undefined,
        "isPartOf": { "@type": "CreativeWorkSeries", "name": `RCKT ${seriesLabel}` }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Archive", "item": `${window.location.origin}/archive` },
          { "@type": "ListItem", "position": 2, "name": art.number_label, "item": canonical }
        ]
      }
    ]
  };

  return (
    <main data-testid="artwork-detail-page" className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-16">
      <Seo title={art.eff_seo_title} description={art.eff_seo_description}
        canonical={canonical} image={art.eff_og_image} type="article"
        noindex={art.published === false} jsonLd={jsonLd} />

      <div className="flex items-center justify-between border-b hairline pb-5 mb-12">
        <Link to="/archive" className="label hover:opacity-50 transition-opacity" data-testid="back-to-archive">← Archive</Link>
        <span className="label">{art.collection === "genesis" ? "Genesis" : art.collection}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-8">
          <div className="bg-white border hairline overflow-hidden">
            <img src={art.image} alt={art.eff_alt_text} className="w-full h-auto" data-testid="artwork-main-image" />
          </div>
          {art.secondary_images?.map((s, i) => (
            <div key={i} className="bg-white border hairline overflow-hidden mt-8">
              <img src={s} alt={`${art.number_label} — ${art.title}, detail ${i + 1}`} className="w-full h-auto" loading="lazy" />
            </div>
          ))}
        </div>

        <aside className="md:col-span-3 md:col-start-10 md:sticky md:top-28 self-start">
          <div className="font-display font-700 text-3xl md:text-4xl tracking-[-0.01em]">{art.number_label}</div>
          <div className="mt-8 space-y-1">
            <h1 className="font-display uppercase tracking-[0.06em] text-lg">{art.title}</h1>
            <div className="text-[var(--muted)]">{art.year}</div>
            <div className="text-[var(--muted)]">{art.medium}</div>
            {art.dimensions && <div className="text-[var(--muted)]">{art.dimensions}</div>}
          </div>

          <div className="mt-10 pt-6 border-t hairline">
            <div className="label mb-2">Type</div>
            <div className="font-display uppercase tracking-[0.06em]">{art.edition}{art.edition === "EDITION" && art.edition_info ? ` — ${art.edition_info}` : ""}</div>
          </div>

          <div className="mt-6 pt-6 border-t hairline">
            <div className="label mb-2">Status</div>
            <div className="font-display uppercase tracking-[0.1em]" data-testid="artwork-status">
              {art.availability === "NFS" ? "Not for sale" : art.availability}
            </div>
          </div>

          {art.instagram_url && (
            <a href={art.instagram_url} target="_blank" rel="noreferrer" data-testid="artwork-instagram"
              aria-label={`${art.number_label} on Instagram`}
              className="inline-flex items-center gap-2 mt-6 pt-6 border-t hairline text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
              <Instagram size={18} strokeWidth={1.6} />
              <span className="label">View on Instagram</span>
            </a>
          )}

          {art.availability === "AVAILABLE" && <EnquiryForm artworkSlug={art.slug} label={art.number_label} />}

          {art.description && <p className="mt-10 text-[var(--muted)] leading-relaxed text-sm">{art.description}</p>}
        </aside>
      </div>

      <nav className="mt-20 pt-6 border-t hairline flex items-center justify-between" data-testid="artwork-nav">
        {art.prev ? (
          <Link to={`/archive/${art.prev.slug}`} className="label hover:opacity-50 transition-opacity" data-testid="artwork-prev">← {art.prev.label}</Link>
        ) : <span />}
        {art.next ? (
          <Link to={`/archive/${art.next.slug}`} className="label hover:opacity-50 transition-opacity" data-testid="artwork-next">{art.next.label} →</Link>
        ) : <span />}
      </nav>
    </main>
  );
}
